// Investor Symposium — Delivery tab (2026-09-03).
//
// Per wave, what do we KNOW about each message we sent? Reads
// GET /api/crm/symposium/events/:id/delivery — one row per send from the
// crm.symposium_sends ledger (migration 027) joined to the trace sweep's
// verdicts and the graded link events — and renders exactly that. Nothing on
// this screen is inferred: a send the sweep has not reached says "not
// traced", a domain nobody has traced has no gateway, and a person who has
// not answered has no RSVP. See delivery-verdict.ts for the evidence table.
//
// client_relations reads this (crm.events.manage), not just admins.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Download } from 'lucide-react'
import {
  SectionCard, Badge, Button, EmptyState, DataTable, ListToolbar,
  type Column, type BadgeTone,
} from '@/components/ui'
import { useApiToken } from '@/auth/useApiToken'
import { api, ApiError } from '@/api/client'
import { num, date as fmtDate } from '@/lib/format'
import { downloadCsv } from '@/lib/csv'
import type { DeliveryReport, DeliveryRow, DeliveryDomain, DeliveryWave, TraceStatus, TraceSweepSummary } from './delivery-types'
import {
  deliveryVerdict, funnelCounts, FUNNEL_MATCH, fmtLocal, fmtUtc, fmtElapsed, type FunnelStep,
} from './delivery-verdict'

const RSVP_TONE: Record<string, BadgeTone> = { yes: 'ok', maybe: 'warn', no: 'alert', none: 'neutral' }
const KIND_LABEL: Record<string, string> = {
  wave: 'wave', invite: 'one-off', invite_test: 'test', event_test: 'test', otp: 'sign-in code',
}

const FUNNEL_STEPS: Array<[label: string, step: FunnelStep, hint: string]> = [
  ['Sent', 'sent', 'Messages handed to the mail transport'],
  ['Accepted by recipient server', 'accepted', 'Their mail server took the message (SMTP 250) — their infrastructure has it; nothing about a person'],
  ['Failed', 'failed', 'Their mail server refused it, it expired, or Exchange Online held it (quarantine / spam filter)'],
  ['Their security scanned the link', 'scanned', 'A scanner fetched the tracked link at or after this send — proves arrival at their security layer'],
  ['Opened (human click)', 'opened', 'A person clicked the link (graded, migration 017). Invite-level: an earlier wave’s open shows here too'],
  ['Visited portal', 'visited', 'The investor portal loaded for a person'],
  ['RSVP’d', 'rsvp', 'Answered yes, maybe or no'],
]

function waveLabel(w: DeliveryWave): string {
  const when = w.sentAt ? fmtDate(w.sentAt, 'med') : w.startedAt ? `${w.status} ${fmtDate(w.startedAt, 'med')}` : w.status
  return `W${w.waveNumber} ${w.name} · ${when}`
}

/** Local time for the eye, the UTC instant in the tooltip. */
function T({ iso, extra }: { iso: string | null | undefined; extra?: string }) {
  if (!iso) return <span className="dim">—</span>
  const title = extra ? `${fmtUtc(iso)}\n${extra}` : fmtUtc(iso)
  return <span className="sym-time" title={title}>{fmtLocal(iso)}</span>
}

function Gateway({ value }: { value: string | null }) {
  return value ? <span className="sym-gw" title="What kind of server accepted the message (from the trace)">{value}</span> : <span className="dim">—</span>
}

export function DeliveryTab({ eventId, eventYear, waveId, onWaveId, trace }: {
  eventId: string
  eventYear: number | string
  waveId: string
  onWaveId: (id: string) => void
  /** From api.symposium.mailboxStatus().trace — absent until the sweep ships. */
  trace: TraceStatus | null | undefined
}) {
  const getToken = useApiToken()
  const [report, setReport] = useState<DeliveryReport | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<{ status: number; message: string } | null>(null)
  const [includeTests, setIncludeTests] = useState(false)
  const [funnelSel, setFunnelSel] = useState<FunnelStep | null>(null)
  const [search, setSearch] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    void (async () => {
      try {
        const r = await api.symposium.delivery(await getToken(), eventId, waveId, includeTests)
        if (!cancelled) { setReport(r); setLoading(false) }
      } catch (e) {
        if (cancelled) return
        setError({ status: e instanceof ApiError ? e.status : 0, message: e instanceof Error ? e.message : String(e) })
        setLoading(false)
      }
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, waveId, includeTests, reloadKey])

  const rows = report?.rows ?? []
  const counts = useMemo(() => funnelCounts(rows), [rows])
  const wave = useMemo(() => report?.waves.find((w) => w.id === waveId) ?? null, [report, waveId])

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase()
    const list = rows.filter((r) => {
      if (funnelSel && !FUNNEL_MATCH[funnelSel](r)) return false
      if (q) {
        const hay = `${r.contactName ?? ''} ${r.company ?? ''} ${r.email} ${r.domain} ${r.gateway ?? ''}`.toLowerCase()
        if (!hay.includes(q)) return false
      }
      return true
    })
    // Investors first, WPI staff after — same separation the rest of the console keeps.
    return [...list.filter((r) => !r.isStaff), ...list.filter((r) => r.isStaff)]
  }, [rows, funnelSel, search])

  const views = useMemo(() => [
    ...(report?.waves ?? []).map((w) => ({ id: w.id, label: waveLabel(w) })),
    { id: 'all', label: 'All sends' },
  ], [report])

  const summaryText = (s: TraceSweepSummary | null): string => s
    ? `${num(s.matched)} of ${num(s.candidates)} matched · ${num(s.accepted)} accepted · ${num(s.failed)} failed · ${num(s.pending)} pending${s.skipped ? ` · ${s.skipped}` : ''}${s.error ? ` · ${s.error}` : ''}`
    : ''
  const traceLine = !trace
    ? { text: 'Message trace: not connected', title: 'The API reports no message-trace status yet', tone: 'neutral' as BadgeTone }
    : !trace.enabled
      ? { text: 'Message trace: off', title: trace.lastError ?? 'The trace sweep runs only in prod with email sending on', tone: 'neutral' as BadgeTone }
      : !trace.connected
        ? { text: 'Message trace: not connected', title: trace.lastError ?? (trace.connected === null ? 'The sweep has not had a send to look for yet' : 'Graph refused the trace call — the ExchangeMessageTrace.Read.All grant is missing'), tone: 'warn' as BadgeTone }
        : {
            text: `Message trace: connected · last sweep ${fmtLocal(trace.lastSweepAt)}`,
            title: [fmtUtc(trace.lastSweepAt), summaryText(trace.lastSummary), trace.lastError].filter(Boolean).join('\n'), tone: 'ok' as BadgeTone,
          }
  const tracedAny = counts.untraced < counts.sent

  const fileStem = `symposium-${eventYear}-delivery-${wave ? `w${wave.waveNumber}` : 'all'}`
  const exportRows = () => downloadCsv(`${fileStem}.csv`, [
    'Contact', 'Investor Group', 'Email', 'Domain', 'Gateway', 'Kind', 'Wave', 'Sent (UTC)', 'Transport',
    'Trace status', 'Accepted (UTC)', 'Accepted by', 'Failed (UTC)', 'Fail reason', 'Scanned (UTC)',
    'Opened (UTC)', 'Visited (UTC)', 'RSVP', 'RSVP at (UTC)', 'Delivery status', 'Data flag', 'WPI staff', 'What we know',
  ], visible.map((r) => [
    r.contactName, r.company, r.email, r.domain, r.gateway, r.kind,
    report?.waves.find((w) => w.id === r.waveId)?.waveNumber ?? null,
    r.sentAt, r.transport, r.traceStatus, r.acceptedAt, r.acceptedBy, r.failedAt, r.failReason,
    r.firstMachineClickAt, r.firstHumanClickAt, r.firstHumanVisitAt, r.rsvp, r.rsvpAt,
    r.deliveryStatus, r.dataFlag, r.isStaff, deliveryVerdict(r).text,
  ]))
  const exportDomains = () => downloadCsv(`${fileStem}-domains.csv`, [
    'Domain', 'Gateway', 'Recipients', 'Sent', 'Accepted', 'Failed', 'Scanned', 'Opened', 'Visited', 'RSVP yes', 'RSVP maybe', 'RSVP no',
  ], (report?.domains ?? []).map((d) => [
    d.domain, d.gateway, d.recipients, d.sent, d.accepted, d.failed, d.scanned, d.clicked, d.visited, d.rsvpYes, d.rsvpMaybe, d.rsvpNo,
  ]))

  const cols: Column<DeliveryRow>[] = [
    {
      key: 'contact', header: 'Contact', sortValue: (r) => r.contactName ?? r.email,
      render: (r) => (
        <span className="sym-contact-cell">
          {r.contactId
            ? <Link className="sym-contact sym-contact--link" to={`/contacts/${r.contactId}`} title="Open the CRM contact record">{r.contactName ?? r.email}</Link>
            : <span className="sym-contact">{r.contactName ?? r.email}</span>}
          {r.isStaff && <Badge tone="navy" variant="outline">WPI</Badge>}
          {r.kind !== 'wave' && <Badge tone="neutral" variant="outline">{KIND_LABEL[r.kind] ?? r.kind}</Badge>}
          {r.dataFlag && <span className="sym-flag" title="Data flag on the invite">{r.dataFlag.replace(/_/g, ' ')}</span>}
        </span>
      ),
    },
    { key: 'group', header: 'Investor Group', sortValue: (r) => r.company ?? '', render: (r) => <span className="muted">{r.company ?? '—'}</span> },
    {
      key: 'domain', header: 'Domain', sortValue: (r) => r.domain,
      render: (r) => <span className="sym-domain-cell"><span title={r.email}>{r.domain || '—'}</span><Gateway value={r.gateway} /></span>,
    },
    { key: 'sent', header: 'Sent', width: 110, sortValue: (r) => r.sentAt, render: (r) => <T iso={r.sentAt} extra={`via ${r.transport}`} /> },
    {
      key: 'accepted', header: 'Accepted', width: 130, sortValue: (r) => r.acceptedAt ?? '',
      render: (r) => r.acceptedAt
        ? <span className="sym-time-pair"><T iso={r.acceptedAt} extra={r.acceptedBy ? `by ${r.acceptedBy}` : undefined} /><span className="dim sym-elapsed">{fmtElapsed(r.sentAt, r.acceptedAt)}</span></span>
        : <span className="dim" title={r.traceStatus == null ? 'Not traced' : `Trace status: ${r.traceStatus}`}>—</span>,
    },
    {
      key: 'failed', header: 'Failed', width: 110, sortValue: (r) => r.failedAt ?? '',
      render: (r) => r.failedAt
        ? <span className="sym-failed"><T iso={r.failedAt} extra={r.failReason ?? undefined} /></span>
        : <span className="dim">—</span>,
    },
    { key: 'scanned', header: 'Scanned', width: 110, sortValue: (r) => r.firstMachineClickAt ?? '', render: (r) => <T iso={r.firstMachineClickAt} extra="First MACHINE click at or after this send" /> },
    { key: 'opened', header: 'Opened', width: 110, sortValue: (r) => r.firstHumanClickAt ?? '', render: (r) => <T iso={r.firstHumanClickAt} extra="First human click on this invite (any wave)" /> },
    { key: 'visited', header: 'Visited', width: 110, sortValue: (r) => r.firstHumanVisitAt ?? '', render: (r) => <T iso={r.firstHumanVisitAt} extra="First human portal visit on this invite (any wave)" /> },
    {
      key: 'rsvp', header: 'RSVP', width: 120, sortValue: (r) => r.rsvp ?? '',
      render: (r) => r.rsvp && r.rsvp !== 'none'
        ? <span className="sym-time-pair"><Badge tone={RSVP_TONE[r.rsvp] ?? 'neutral'} variant="soft" dot>{r.rsvp}</Badge><T iso={r.rsvpAt} /></span>
        : <span className="dim">{r.rsvp === 'none' ? 'none' : '—'}</span>,
    },
    {
      key: 'verdict', header: 'What we know', sortValue: (r) => deliveryVerdict(r).key,
      render: (r) => { const v = deliveryVerdict(r); return <span className={`sym-verdict sym-verdict--${v.tone}`}>{v.text}</span> },
    },
  ]

  const domainCols: Column<DeliveryDomain>[] = [
    { key: 'domain', header: 'Domain', sortValue: (d) => d.domain, render: (d) => <span className="sym-contact">{d.domain}</span> },
    { key: 'gateway', header: 'Gateway', sortValue: (d) => d.gateway ?? '', render: (d) => <Gateway value={d.gateway} /> },
    { key: 'recipients', header: 'Recipients', width: 90, align: 'right', sortValue: (d) => d.recipients, render: (d) => num(d.recipients) },
    { key: 'sent', header: 'Sent', width: 70, align: 'right', sortValue: (d) => d.sent, render: (d) => num(d.sent) },
    { key: 'accepted', header: 'Accepted', width: 90, align: 'right', sortValue: (d) => d.accepted, render: (d) => num(d.accepted) },
    { key: 'failed', header: 'Failed', width: 70, align: 'right', sortValue: (d) => d.failed, render: (d) => d.failed ? <span className="sym-failed">{num(d.failed)}</span> : <span className="dim">0</span> },
    { key: 'scanned', header: 'Scanned', width: 80, align: 'right', sortValue: (d) => d.scanned, render: (d) => num(d.scanned) },
    { key: 'clicked', header: 'Opened', width: 80, align: 'right', sortValue: (d) => d.clicked, render: (d) => num(d.clicked) },
    { key: 'visited', header: 'Visited', width: 80, align: 'right', sortValue: (d) => d.visited, render: (d) => num(d.visited) },
    {
      key: 'rsvp', header: 'RSVPs', width: 130, align: 'right', sortValue: (d) => d.rsvpYes * 1000 + d.rsvpMaybe,
      render: (d) => <span className="num" title={`${d.rsvpYes} yes · ${d.rsvpMaybe} maybe · ${d.rsvpNo} no`}>{d.rsvpYes} / {d.rsvpMaybe} / {d.rsvpNo}</span>,
    },
  ]

  const stepTitle = funnelSel ? FUNNEL_STEPS.find((s) => s[1] === funnelSel)?.[0] ?? 'Not traced' : null

  return (
    <div className="sym-stack">
      <ListToolbar
        views={views}
        activeView={waveId}
        onView={(id) => { setFunnelSel(null); onWaveId(id) }}
        search={search}
        onSearch={setSearch}
        searchPlaceholder="Name, company, email, domain…"
        count={visible.length}
        onExport={report ? exportRows : undefined}
        extra={
          <div className="sym-delivery-extra">
            <span title={traceLine.title}><Badge tone={traceLine.tone} variant="outline">{traceLine.text}</Badge></span>
            <label className="sym-delivery-tests">
              <input type="checkbox" checked={includeTests} onChange={(e) => setIncludeTests(e.target.checked)} />
              Include test &amp; sign-in-code sends
            </label>
          </div>
        }
      />

      {loading && <div className="dim" style={{ padding: 24 }}>Loading delivery report…</div>}
      {!loading && error && (
        <EmptyState
          title={error.status === 404 ? 'That wave is not on this event' : 'Could not load the delivery report'}
          hint={error.message}
          action={error.status === 404
            ? <Button variant="default" onClick={() => onWaveId('all')}>Show all sends</Button>
            : <Button variant="default" onClick={() => setReloadKey((k) => k + 1)}>Retry</Button>}
        />
      )}

      {!loading && !error && report && (
        <>
          <SectionCard
            eyebrow={wave
              ? `Wave ${wave.waveNumber} · ${wave.name}${wave.emailKind ? ` · ${wave.emailKind.replace(/_/g, ' ')}` : ''} · ${wave.status}${wave.sentAt ? ` ${fmtDate(wave.sentAt, 'med')}` : ''} · sender counted ${wave.sentCount ?? '—'} sent / ${wave.failedCount ?? '—'} failed · ledger holds ${num(counts.sent)}`
              : `Every send on this event · ${num(counts.sent)} in the ledger · click a step to see who`}
            title={<>Delivery <b>Funnel</b></>}
            right={
              <button
                className={`sym-funnel-chip${funnelSel === 'untraced' ? ' active' : ''}`}
                onClick={() => setFunnelSel(funnelSel === 'untraced' ? null : 'untraced')}
                title={!report.traceEnabled
                  ? 'Message tracing is not enabled in this environment — nothing here can be traced'
                  : tracedAny
                    ? 'Sends the trace sweep has not stamped (a trace lags 5–10 minutes; a send older than 90 days is out of reach)'
                    : 'The trace sweep has not stamped any of these sends yet'}
              >
                {num(counts.untraced)} not traced
              </button>
            }
          >
            {rows.length === 0
              ? <EmptyState title="No sends recorded" hint={wave ? 'The ledger has no messages for this wave.' : 'The ledger has no messages for this event. Sends made before 2026-09-03 appear once migration 027 backfills them.'} />
              : (
                <div className="sym-funnel sym-funnel--delivery">
                  {FUNNEL_STEPS.map(([label, step, hint]) => (
                    <button
                      key={step}
                      className={`sym-funnel-step${funnelSel === step ? ' active' : ''}${step === 'failed' && counts.failed > 0 ? ' sym-funnel-step--alert' : ''}`}
                      onClick={() => setFunnelSel(funnelSel === step ? null : step)}
                      title={`${hint}. Show these ${num(counts[step])} below.`}
                    >
                      <span className="sym-funnel-v num">{num(counts[step])}</span>
                      <span className="sym-funnel-l label">{label}</span>
                    </button>
                  ))}
                </div>
              )}
          </SectionCard>

          <SectionCard
            eyebrow={stepTitle ? `Filtered: ${stepTitle}` : 'Every message, what the trace and the link know about it'}
            title={<>Recipient <b>Delivery</b></>}
            right={funnelSel && <Button variant="default" size="sm" onClick={() => setFunnelSel(null)}>Show all</Button>}
          >
            {visible.length === 0
              ? <EmptyState title="Nobody here" hint={funnelSel ? 'This step is empty for the selected sends.' : 'No sends match.'} />
              : <DataTable rows={visible} columns={cols} getRowKey={(r) => r.sendId} dense />}
          </SectionCard>

          <SectionCard
            eyebrow="By recipient domain · gateway from the trace, blank until traced"
            title={<>Domain <b>Rollup</b></>}
            right={<Button variant="default" size="sm" icon={<Download size={14} />} onClick={exportDomains}>Export domains</Button>}
          >
            {report.domains.length === 0
              ? <EmptyState title="No domains" hint="Nothing has been sent for this selection." />
              : <DataTable rows={report.domains} columns={domainCols} getRowKey={(d) => d.domain} initialSort={{ key: 'sent', dir: 'desc' }} dense />}
          </SectionCard>
        </>
      )}
    </div>
  )
}
